import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminRequest, getAdminHeaders, toAssetUrl } from "./adminApi";
import { formatCityLabel } from "../../utils/resolveRegion";

const VERIFY_FILTERS = [
  { value: "all", label: "全部" },
  { value: "pending", label: "待审核" },
  { value: "unverified", label: "未认证" },
  { value: "phone", label: "手机认证" },
  { value: "real_name", label: "实名认证" },
  { value: "face", label: "真人认证" },
  { value: "profile", label: "资料认证" },
  { value: "platform", label: "平台认证" },
];

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [verifyFilter, setVerifyFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUsers();
  }, [verifyFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const qs =
        verifyFilter && verifyFilter !== "all"
          ? `?verification_filter=${encodeURIComponent(verifyFilter)}`
          : "";
      const { res, data } = await adminRequest(`/api/admin/users${qs}`, {
        headers: getAdminHeaders(),
      });

      if (!res.ok) {
        setError(data?.detail || "加载用户列表失败");
        setUsers([]);
        return;
      }

      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("无法连接后台服务，请确认后端已启动");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${user.id}/toggle-status`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "操作失败");
        return;
      }

      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("操作失败");
    }
  };

  const toggleUserAvailable = async (user) => {
    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${user.id}/toggle-available`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "操作失败");
        return;
      }

      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("操作失败");
    }
  };

  const toggleUserVerify = async (user) => {
    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${user.id}/verify`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || "操作失败");
        return;
      }

      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("操作失败");
    }
  };

  const deleteUser = async (user) => {
    const name = user.name || user.phone || `ID ${user.id}`;
    if (
      !window.confirm(
        `确定删除用户「${name}」吗？\n此操作不可恢复，关联的消息和资料也会一并删除。`
      )
    ) {
      return;
    }

    try {
      const { res, data } = await adminRequest(
        `/api/admin/users/${user.id}/delete`,
        {
          method: "POST",
          headers: getAdminHeaders(),
        }
      );

      if (!res.ok) {
        alert(data?.detail || data?.msg || "删除失败");
        return;
      }

      fetchUsers();
    } catch (err) {
      console.error(err);
      alert("删除失败");
    }
  };

  const filteredUsers = users.filter((item) => {
    const text = `${item.name || ""} ${item.phone || ""} ${item.city || ""}`;
    return text.toLowerCase().includes(keyword.toLowerCase());
  });

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">用户管理</h1>
          <p className="text-gray-400 mt-1">查看用户资料、分层认证状态和账号状态</p>
        </div>

        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索姓名、手机号、城市"
          className="w-full md:w-80 border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {VERIFY_FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => setVerifyFilter(item.value)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              verifyFilter === item.value
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="mb-4 rounded-xl bg-red-50 text-red-600 px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-gray-400">加载中...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-gray-400">暂无用户数据</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left p-4">用户</th>
                  <th className="text-left p-4">城市</th>
                  <th className="text-left p-4">手机</th>
                  <th className="text-left p-4">实名</th>
                  <th className="text-left p-4">真人</th>
                  <th className="text-left p-4">资料</th>
                  <th className="text-left p-4">平台</th>
                  <th className="text-left p-4">等级</th>
                  <th className="text-left p-4">前台</th>
                  <th className="text-right p-4">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                        className="flex items-center gap-3 text-left hover:opacity-80"
                      >
                        <img
                          src={toAssetUrl(user.avatar) || "/default-avatar.png"}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover bg-gray-100"
                        />
                        <div>
                          <p className="font-bold text-gray-800 hover:text-blue-600">
                            {user.name || "未命名用户"}
                          </p>
                          <p className="text-gray-400">{user.phone || "-"}</p>
                          {(user.profile_pending || user.id_pending) && (
                            <p className="text-xs text-amber-600 mt-0.5">有待审项</p>
                          )}
                        </div>
                      </button>
                    </td>
                    <td className="p-4 text-gray-600">
                      {formatCityLabel(user.province, user.city, user.district, "-")}
                    </td>
                    <td className="p-4">
                      <StatusBadge ok={user.phone_verified} />
                    </td>
                    <td className="p-4">
                      <StatusBadge ok={user.real_name_verified || user.is_id_verified} />
                    </td>
                    <td className="p-4">
                      <StatusBadge ok={user.face_verified} />
                    </td>
                    <td className="p-4">
                      {user.profile_pending ? (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                          待审
                        </span>
                      ) : (
                        <StatusBadge ok={user.profile_verified} />
                      )}
                    </td>
                    <td className="p-4">
                      <StatusBadge ok={user.platform_verified} />
                    </td>
                    <td className="p-4 text-gray-700">
                      L{user.verification_level ?? 0}
                      <span className="text-gray-400 text-xs ml-1">
                        {user.verification?.verification_level_label || ""}
                      </span>
                    </td>
                    <td className="p-4">
                      {user.is_available ? (
                        <span className="text-green-600">展示中</span>
                      ) : (
                        <span className="text-gray-400">已隐藏</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="px-4 py-2 rounded-xl border border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          认证详情
                        </button>
                        <button
                          onClick={() => toggleUserAvailable(user)}
                          className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                          {user.is_available ? "隐藏前台" : "前台展示"}
                        </button>
                        <button
                          onClick={() => toggleUserVerify(user)}
                          className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                          {user.is_id_verified ? "取消实名" : "实名通过"}
                        </button>
                        <button
                          onClick={() => toggleUserStatus(user)}
                          className="px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                          {user.is_disabled ? "启用" : "禁用"}
                        </button>
                        <button
                          onClick={() => deleteUser(user)}
                          className="px-4 py-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ ok }) {
  return ok ? (
    <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">
      ✓
    </span>
  ) : (
    <span className="text-xs bg-gray-100 text-gray-400 px-2 py-1 rounded-full">
      ○
    </span>
  );
}
